import glCodeService from 'Services/api/glCode.service'
import locationService from 'Services/api/location.service'
import productService from 'Services/api/product.service'
import productCategoryService from 'Services/api/productCategory.service'
import taxService from 'Services/api/tax.service'
import uomService from 'Services/api/uom.service'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import { IOption } from 'interfaces'
import { UOMContract, TaxContract, ItemContract } from 'models'
import { useProductContext } from 'pages/ProductsPage/Products/ProductContextProvider'
import { useMemo, useEffect, useCallback, useState } from 'react'

export type TIngredientOption = {
    ingredientID: string
    name: string
    uomid: number
    cost: number
}

export const uomToOption = (item: UOMContract) =>
    ({
        label: `${item.symbol} ${item.name ? ` (${item.name})` : ''}`,
        value: item.uomid,
    } as IOption)

export const taxToOption = (item: TaxContract) => ({
    value: item.taxID,
    label:
        `${item.name}: ${item.rate?.toFixed(2)}%` +
        (item.taxComponents && item.taxComponents.length > 0
            ? ` (${item.taxComponents
                  .map((t) => `${t.name} ${t.rate?.toFixed(2)}%`)
                  .join(', ')})`
            : ''),
})

function useCommonProductEditDependencies(
    productID?: string,
    noTax: boolean = false,
) {
    const [
        categories = [],
        fetchCategories,
        fetchCategoriesStatus,
    ] = useFetchApi(productCategoryService.getProductCategories)

    const [locations = [], fetchLocations, getLocationsStatus] = useFetchApi(
        locationService.getLocations,
    )

    const [taxes = [], fetchTaxes, fetchTaxesStatus] = useFetchApi(
        taxService.getTaxes,
    )

    const [uomLists = [], fetchUomLists, fetchUomStatus] = useFetchApi(
        uomService.getAllUOMs,
    )

    const [product, fetchProduct, fetchProductStatus] = useFetchApi(
        productService.getProduct,
    )

    const [glcodes = [], fetchGLCodes, fetchGLCodeStatus] = useFetchApi(
        glCodeService.getGLCodes,
    )

    const { dependencies } = useProductContext()

    const [duplicatedNameList, setDuplcatedNameList] = useState<string[]>([])

    const updateDuplicatedNameList = useCallback(
        (id?: string) => {
            const products = dependencies.products ?? []
            const duplcaitedProductNames = products
                .filter((p) => !id || p.itemID !== id)
                .flatMap((i) => i.name!)
            setDuplcatedNameList(duplcaitedProductNames)
        },
        [dependencies.products],
    )

    const defaultProduct = useMemo<ItemContract>(() => {
        return product
            ? {
                  ...product,
              }
            : {
                  name: '',
                  description: '',
                  barcode: '',
                  uomid: 1,
                  isActive: true,
                  locationItems: [
                      {
                          // No tax for Deposit-and-Credit product
                          taxID:
                              !noTax && taxes.length === 1
                                  ? taxes.map(taxToOption)[0].value
                                  : '',
                          isTaxInclusive: !noTax && true,
                      },
                  ],
              }
    }, [product, taxes, noTax])

    const [, initLocationDataApi, initLocationDataStatus] = useFetchApi(
        productService.initLocationData,
    )

    const loadInitialProduct = useCallback(
        async (itemID?: string) => {
            if (itemID) {
                await fetchProduct(itemID)
            }
            updateDuplicatedNameList(itemID)
        },
        [fetchProduct, updateDuplicatedNameList],
    )

    const initLocationData = useCallback(async () => {
        if (productID) {
            await initLocationDataApi(productID)
            await loadInitialProduct(productID)
        }
    }, [loadInitialProduct, initLocationDataApi, productID])

    useEffect(() => {
        fetchCategories()
        fetchTaxes()
        fetchUomLists()
        fetchGLCodes()
        fetchLocations()
    }, [
        fetchCategories,
        fetchLocations,
        fetchTaxes,
        fetchUomLists,
        fetchGLCodes,
    ])

    useEffect(() => {
        loadInitialProduct(productID)
    }, [loadInitialProduct, productID])

    return {
        loading: isLoading([
            fetchTaxesStatus,
            fetchUomStatus,
            fetchProductStatus,
            fetchCategoriesStatus,
            fetchGLCodeStatus,
            getLocationsStatus,
        ]),
        uomLists: uomLists.map(uomToOption),
        fetchUomStatus,
        product: defaultProduct,
        glcodes,
        uoms: uomLists,
        productCategories: categories,
        initLocationData,
        initLocationDataStatus,
        locations,
        duplicatedNameList,
        // Update duplicate name list when there are error
        addDuplicatedName: (name: string) =>
            setDuplcatedNameList((prev) => [...prev, name]),
        taxes,
        fetchTaxesStatus,
    } as const
}

export default useCommonProductEditDependencies
